import {useCallback, useEffect, useMemo, useRef} from 'react';
import {useLocation, useSearchParams} from 'react-router-dom';

/** What survives a round trip through a query string. */
export type QueryValue = string | number | boolean | undefined;

/**
 * The query string each list route was last looked at with.
 *
 * The browser's own Back needs none of this - the query is in the history entry, so it restores the
 * filtered list by itself. The breadcrumb in `AMPNavbar` is not Back: it is a `Link` to the bare list
 * route, and that is what dropped the filter when someone left a detail page by the control the page
 * itself offers rather than by the browser's.
 *
 * Module-level rather than in the store, deliberately. It is one line of UI memory, it means something
 * only while the app is open, and it must never be persisted or rehydrated - a list opened from the
 * menu after a reload has to come up unfiltered, or nobody can find the unfiltered list again. Keyed
 * by pathname, so two list pages cannot overwrite each other's.
 */
const lastListQuery = new Map<string, string>();

/** The query a list route was last seen with, or an empty string if it has not been seen. */
export const rememberedListQuery = (pathname: string) => lastListQuery.get(pathname) ?? '';

/**
 * A list's own state - its filters, its sort, its page - kept in the address bar.
 *
 * State in `useState` is gone the moment the page unmounts, and opening one row and coming back is
 * exactly that: the list returns unfiltered, on page one, and the narrowing has to be done again. The
 * query string is the one place that already survives it. The browser keeps it in the history entry,
 * so Back restores the whole view with nothing of ours to persist and nothing to invalidate; a reload
 * keeps it; and a narrowed list becomes a link somebody can be sent.
 *
 * It wins over the alternatives on the same ground. `sessionStorage`, or a slice of the store, would
 * restore the view too - but only for the person who set it, only until the tab closes, and at the
 * price of a list that comes back mysteriously filtered the next time it is opened from the menu. The
 * address bar says what it is showing, which is the whole point.
 *
 * Two rules keep it honest:
 * - a value equal to its default is left out, so an untouched list has a clean URL and a filtered one
 *   shows precisely what was touched;
 * - a change **replaces** the current history entry rather than pushing one, because six letters typed
 *   into a search box must not cost six presses of Back to leave the page.
 *
 * `defaults` is read for the name and the type of every field, so it has to be a stable object:
 * declare it at module scope, never inside the component.
 */
export const useListQueryState = <T extends Record<string, QueryValue>>(defaults: T) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const {pathname} = useLocation();

    // Remembered as well as read, for the breadcrumb that leads back here.
    useEffect(() => {
        lastListQuery.set(pathname, searchParams.toString());
    }, [pathname, searchParams]);

    const value = useMemo(() => {
        const result = {...defaults};
        (Object.keys(defaults) as (keyof T & string)[]).forEach((key) => {
            const raw = searchParams.get(key);
            if (raw === null) {
                return;
            }
            const fallback = defaults[key];
            // Read back as whatever the default is. A field whose default is `undefined` carries no
            // type to be read by and comes back as the string it was written as - which is what a
            // status, or any other enum, wants anyway. A number that does not parse is refused rather
            // than passed on as NaN: the URL can be typed by hand, and the server answers 400 to one.
            if (typeof fallback === 'number') {
                const parsed = Number(raw);
                result[key] = (raw.trim() && Number.isFinite(parsed) ? parsed : fallback) as T[keyof T & string];
                return;
            }
            result[key] = (typeof fallback === 'boolean' ? raw === 'true' : raw) as T[keyof T & string];
        });
        return result;
    }, [searchParams, defaults]);

    /**
     * The last value written, so two writes in the same tick compose instead of overwriting.
     *
     * One control can set two fields through two callbacks - the period select hands over the preset
     * and the dates it computed, one after the other - and a second writer spreading the `value` of
     * the render it was created in would put the first writer's field back as it was. This router
     * version has no functional `setSearchParams`, so the composition is kept here.
     */
    const latest = useRef(value);
    latest.current = value;

    const write = useCallback((next: T | ((current: T) => T)) => {
        const resolved = typeof next === 'function' ? next(latest.current) : next;
        latest.current = resolved;
        const query: Record<string, string> = {};
        (Object.keys(resolved) as (keyof T & string)[]).forEach((key) => {
            const item = resolved[key];
            if (item === undefined || item === '' || item === defaults[key]) {
                return;
            }
            query[key] = String(item);
        });
        setSearchParams(query, {replace: true});
    }, [defaults, setSearchParams]);

    return [value, write] as const;
};
